import { getRestorationZoneDemandRestoredData } from "api/dataVisualisation/restorationZone/restorationZoneDemandRestored";
import FullWidthSection from "components/components/FullWidthSection/FullWidthSection";
import { StyledDiv } from "components/components/SectionWithErrorHandling/style";
import SubmissionDateTimeFilter from "components/components/SubmissionDateTimeFilter/SubmissionDateTimeFilter";
import TableWithErrorHandling from "components/components/TableWithErrorHandling/TableWithErrorHandling";
import DataExporter from "components/dataVisualization/dataExporterComponents/DataExporter/DataExporter";
import RestorationZoneDemandRestoredTable from "components/dataVisualization/restorationZone/DemandRestored/RestorationZoneDemandRestoredTable/RestorationZoneDemandRestoredTable";
import useRestorationZoneDemandRestoredData from "components/dataVisualization/restorationZone/DemandRestored/useRestorationZoneDemandRestoredData";
import {
  Region,
  RegionFilter,
} from "components/dataVisualization/restorationZone/RegionFilter";
import { TableFooter } from "components/dataVisualization/tableComponents/style";
import { useChartFocussedDateContext } from "contexts/ChartFocussedDateContext";
import { useDataExport } from "contexts/DataExportContext";
import { DataDownloadFormat } from "models/dataDownload/dataDownloadFormat";
import DateFilterModel from "models/filterModels/dateFilterModel";
import {
  RestorationZoneDemandRestoredModel,
  RestorationZoneDemandRestoredTableModel,
} from "models/restorationZone/restorationZoneDemandRestored";
import React, { useEffect, useMemo, useState } from "react";
import { downloadDataToFile } from "utils/DataDownloads/FetchDataForFileExport/DownloadDataToFile";
import {
  addDaysToDate,
  ceilToHalfHour,
  floorToHalfHour,
  getEarlier,
} from "utils/dateHelpers";

export const mapToRestorationZoneDemandRestoredRow = (
  data: RestorationZoneDemandRestoredModel[]
): RestorationZoneDemandRestoredTableModel[] =>
  data.map((item, key) => ({
    id: key,
    submissionDateTime: new Date(item.submissionDateTime),
    region: item.region,
    gspGroupId: item.gspGroupId,
    restoredDemand: item.restoredDemand,
  }));

const RestorationZoneDemandRestoredContainer: React.FC = () => {
  const [regionFilter, setRegionFilter] = useState<Region>(Region.AllRegions);

  const defaultEndTime = floorToHalfHour(new Date());
  const defaultDateFilter = useMemo(
    () =>
      new DateFilterModel(addDaysToDate(defaultEndTime, -7), defaultEndTime),
    [defaultEndTime]
  );

  const { focussedDate } = useChartFocussedDateContext();

  const [dateFilter, setDateFilter] =
    useState<DateFilterModel>(defaultDateFilter);

  const {
    data: rawData,
    fetchData,
    status,
  } = useRestorationZoneDemandRestoredData(dateFilter, regionFilter);

  useEffect(fetchData, [fetchData]);

  const tableData = useMemo(
    () => (rawData ? mapToRestorationZoneDemandRestoredRow(rawData) : []),
    [rawData]
  );

  const { dispatch: dataExportCountDispatch } = useDataExport();

  const downloadFile = (fileFormat: DataDownloadFormat): void => {
    downloadDataToFile(
      () =>
        getRestorationZoneDemandRestoredData(
          dateFilter,
          regionFilter,
          fileFormat
        ),
      `RestorationZoneDemandRestored-${dateFilter.startDate.toISOString()}-${dateFilter.endDate.toISOString()}`,
      fileFormat,
      dataExportCountDispatch
    );
  };

  useEffect(() => {
    if (focussedDate === undefined) {
      return;
    }

    // set table date filter to focus on focussedDate,
    // including the previous week and the following day
    setDateFilter(
      new DateFilterModel(
        addDaysToDate(focussedDate, -7),
        getEarlier(addDaysToDate(focussedDate, 1), ceilToHalfHour(new Date()))
      )
    );
  }, [focussedDate]);

  return (
    <>
      <FullWidthSection className="is-grey-section">
        <StyledDiv className="container px-6">
          <SubmissionDateTimeFilter
            dateFilter={dateFilter}
            setDateFilter={setDateFilter}
          />
        </StyledDiv>
      </FullWidthSection>
      <RegionFilter
        regionFilter={regionFilter}
        setRegionFilter={setRegionFilter}
      />
      <TableWithErrorHandling
        data={tableData}
        dataFetchStatus={status}
        rerequestData={fetchData}
      >
        <RestorationZoneDemandRestoredTable data={tableData} />
      </TableWithErrorHandling>
      <TableFooter rightAligned>
        <DataExporter downloadData={downloadFile} />
      </TableFooter>
    </>
  );
};

export default RestorationZoneDemandRestoredContainer;
